import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import CommentsList from "@Components/Comments/CommentsList";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import CustomFields from "@Components/IssuesCustomFields/CustomFields";
import { defaultModulesMapper, folderPermissions, getViewerUrl } from "@Helpers";
import { getIssueViewerUrl } from "../../../utils/issueViewerNavigation";
import { getIssuesData, fetchIssueById, isIssueRequiredPermission } from "@Features/issues/state/bcfData.actions";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import { useNavigate } from "react-router-dom";
import IssueStatusPopover from "../../IssueStatusPopover/IssueStatusPopover";
import { GrClose } from "react-icons/gr";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getIconFromFieldType } from "@Features/portal-settings";
import { useTranslation } from "@i18n";
import { getImageUrl } from "@Utils";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";
import Text from "@CubeComponents/html/Text/Text";
import ModuleUsersAccess from "@Components/ModuleUsersAccess";
import { BiLinkExternal } from "react-icons/bi";
import type { IssueItem } from "@ServerTypes/issues/issues.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { AppDispatch, RootState } from "@Reducers/index";
const { RESOURCE_COMMENT } = folderPermissions();
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";

type FormTemplateSection = NonNullable<IssueItem["formTemplate"]>[number];
type FormTemplateQuestion = NonNullable<FormTemplateSection["questionFields"]>[number];
type FormTemplateAnswer = FormTemplateQuestion["answer"];

interface AttachedFile {
  name: string;
  fileVersion: string;
  resourceType: string;
}

/**
 * One selected row as this panel reads it. The row is assembled by whichever grid owns the
 * selection — the Issues grid, `IssuesInReview`, or a file-manager row — so every field is optional
 * and only the ones read below are named. On a transmittal-issue row `issueId`/`fileVersion` stand
 * in for `id`/`fileVersionId`.
 */
interface IssueDetailsRow {
  id?: ObjectIdString;
  issueId?: ObjectIdString;
  transmittalVersionId?: string | null;
  spaceId?: ObjectIdString;
  fileVersionId?: ObjectIdString;
  fileVersion?: ObjectIdString;
  formTemplate?: IssueItem["formTemplate"];
}

interface IssueDetailsProps {
  rowData: IssueDetailsRow[];
  allowCloseBtn?: boolean;
  closePropertiesCallback?: () => void;
  updateGridRowData?: (id: string, updates: Record<string, unknown>) => void;
}

type SideBarState = "no-records" | "multiple-records" | "display-row";

interface AccordionState {
  access: boolean;
  customFields?: boolean;
}

function IssueDetails({
  rowData: selectedRecordsData,
  allowCloseBtn = false,
  closePropertiesCallback,
  updateGridRowData,
}: IssueDetailsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [rowData, setRowData] = useState<IssueDetailsRow>({});
  const [sideBarState, setSideBarState] = useState<SideBarState>("no-records");

  useEffect(() => {
    // Update rowData
    setRowData(selectedRecordsData.length === 1 ? selectedRecordsData[0]! : {});

    // Update sideBarState
    if (selectedRecordsData.length === 0) {
      setSideBarState("no-records");
    } else if (selectedRecordsData.length > 1) {
      setSideBarState("multiple-records");
    } else if (selectedRecordsData.length === 1) {
      setSideBarState("display-row");
    }
  }, [selectedRecordsData]);

  const [accordionState, setAccordionState] = useState<AccordionState>({ access: false });

  const fileVersionId = rowData.transmittalVersionId ? rowData.fileVersion : rowData.fileVersionId;
  // Both branches are optional on the row type, but everything below only renders for a real
  // selected issue, so the id is present. One erased assertion at the source instead of one per read.
  const issueId = (rowData.transmittalVersionId ? rowData.issueId : rowData.id)!;
  const { spaceId } = rowData;

  const issues = useSelector((state: RootState) => state.issues);
  const issue = issues[issueId] as IssueItem;

  // Select the stored array (a stable reference) and derive OUTSIDE the selector. Mapping inside it
  // built a new array of new objects on every call, so useSelector's identity check never held and
  // this component re-rendered on every dispatched action. `shallowEqual` would not have helped —
  // the mapped elements are fresh objects too.
  const storedAttachedFiles = useSelector((state: RootState) => state.issues[issueId]?.attachedFiles);
  const attachedFiles = useMemo<AttachedFile[]>(
    () =>
      (storedAttachedFiles ?? []).map((file) => ({
        name: file.originalName ?? "",
        fileVersion: file.fileVersionId ?? "",
        resourceType: file.resourceType ?? "",
      })),
    [storedAttachedFiles],
  );

  const handleOpenFileLink = () => {
    // The helper takes a full `IssueItem`; a selected row carries only the four fields it reads
    // (`fileVersionId`, `id`, `spaceId`, `projectUniqueId`). Erased assertion at the boundary.
    const location = getIssueViewerUrl(rowData as typeof rowData & IssueItem);
    if (location) {
      navigate(location);
    }
  };

  const closePropertiesHandler = () => {
    if (closePropertiesCallback) {
      closePropertiesCallback();
    }
  };

  const init = useCallback(() => {
    const params = { issueId };
    dispatch(getIssuesData(params));
  }, [issueId]);

  // After a status change (from the status popover or the Reviews accordion) re-fetch the issue so the
  // panel reflects the authoritative status (possibly "Varies"), then refresh the grid row to match.
  const refreshIssueAndGrid = async () => {
    const fresh = await dispatch(fetchIssueById(issueId));
    if (fresh) {
      updateGridRowData?.(issueId, { statusInReview: fresh.statusInReview });
    }
  };

  useEffect(() => {
    init();
  }, [init]);

  const handleOpenLink = (dependency: AttachedFile, newTab: boolean) => {
    const location = getViewerUrl({
      fileVersionId: dependency.fileVersion,
      spaceId: issue.spaceId,
      projectId: issue.projectUniqueId,
    });
    if (location) {
      newTab ? window.open(location, "_blank") : navigate(location);
    }
  };

  const accessHeader = () => (
    <div>
      <Text>{t("Access")}</Text>
    </div>
  );

  const customFieldsHeader = () => (
    <div>
      <Text>{t("Custom Fields")}</Text>
    </div>
  );

  const accessContent = () => {
    return (
      <ModuleUsersAccess
        module={defaultModulesMapper.ISSUES}
        resourceId={issueId}
        // issue.users is `unknown[]` server-side (issues.schema users: z.array(z.unknown())); bridge to the component's shape
        users={(issue.users ?? []) as React.ComponentProps<typeof ModuleUsersAccess>["users"]}
        spaceId={issue.spaceId}
      />
    );
  };
  const getAnswerValue = (answer: FormTemplateAnswer, fieldType: FormTemplateQuestion["fieldType"]): string => {
    if (!answer) return "-";

    const value = answer.value;

    if (fieldType === "date-time") {
      return convertDate(Number(value), "NormalDate") || "-";
    }
    if (Array.isArray(value)) {
      return value
        .map((item: unknown) => {
          if (typeof item === "object") {
            return (item as { name?: string }).name;
          }
          return item;
        })
        .join(", ");
    }
    // `answerSchema.value` is `z.unknown()` — its shape follows the question's `fieldType`; the
    // remaining types all render as text.
    return (value as string | undefined) || "-";
  };

  const customFieldsContent = () => (
    <div className="flex flex-col py-2 gap-8">
      {rowData.formTemplate?.map((section: FormTemplateSection) => (
        <div key={section._id}>
          <div className="font-semibold text-base">{section.sectionHeading}</div>
          <div className="flex flex-col gap-6">
            {/* KNOWN BUG, preserved verbatim: `questionFields` is optional on the template schema and
                is dereferenced without a guard. Erased assertion; a `?.` would change behaviour. */}
            {section.questionFields!.map((field: FormTemplateQuestion) => (
              <div key={field.questionValue} className="flex">
                <div className="flex flex-1 justify-self-start font-semibold text-sm gap-2">
                  <span>{getIconFromFieldType(field.fieldType)}</span>
                  {field.questionValue}
                </div>
                <div className="flex-1 justify-self-start text-sm ms-6">
                  {getAnswerValue(field.answer, field.fieldType)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const getSnapshotUrl = () => {
    let url = `${getImageUrl()}/Issues_default_image.svg`;
    if (issue?.snapshot) {
      url = getUrlForImage({ id: issueId }, "IssueImage");
    }
    return url;
  };

  const renderSideBar = () => (
    <div className="h-full w-160 overflow-auto text-sm">
      {issue && (
        <div className="overflow-auto border border-[#c6d8ec] p-4 border-e-8 h-full">
          <div className="grid grid-cols-[auto_auto] justify-between">
            {rowData.fileVersionId && (
              <ButtonComponent type="button" buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleOpenFileLink}>
                {t("View in Viewer")}
              </ButtonComponent>
            )}
            {allowCloseBtn ? <GrClose size={14} onClick={closePropertiesHandler} /> : undefined}
          </div>
          <div className="grid grid-cols-[auto_auto] justify-between py-5">
            <div className="font-bold">{issue.title}</div>
            <div>
              <ToolTipComponent
                showTipPointer
                position="bottom"
                content={convertDate(Number(issue.createdDate), "dateForText")}
              >
                {convertDate(Number(issue.createdDate), "dateForTooltip")}
              </ToolTipComponent>
            </div>
          </div>
          <div className="pt-3">
            <img src={getSnapshotUrl()} className="h-[11.625rem] w-[22.5rem] object-cover" alt="snapshot" />
          </div>
          <div className="pt-3">
            <SimpleEditor value={sanitizeHtml(issue.description)} editable={false} showToolbar={false} />
          </div>
          <div className="mt-8 flex flex-col gap-4 text-sm">
            <div className="flex items-center gap-8">
              <Text className="font-bold">{t("Created by")}</Text>
              <span>{issue.createdByName}</span>
            </div>
            {issue.assignedTo?.length ? (
              <div className="flex items-center gap-6">
                <Text className="font-bold">{t("Assigned To")}</Text>
                <UserNameTagComponent
                  showAvatarOnly={issue.assignedTo.length != 1}
                  userIdsForGroup={
                    issue.assignedTo.length == 1
                      ? undefined
                      : issue.assignedTo.map((u: NonNullable<IssueItem["assignedTo"]>[number]) => u._id)
                  }
                  id={issue.assignedTo.length == 1 ? issue.assignedTo[0]?._id : undefined}
                  maxAvatars={issue.assignedTo.length == 1 ? undefined : 5}
                  size="XXS"
                  isCircle
                  enableDetailsModalForGroupedUsers
                  detailsModalHeader={t("Assigned To")}
                />
              </div>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-8">
              <Text className="font-bold">{t("Status")}</Text>
              <IssueStatusPopover
                issue={{
                  id: issueId,
                  // Optional on the row type (see `IssueDetailsRow`) but required by the popover,
                  // which cannot resolve the issue's reviews without it. Erased assertion.
                  spaceId: spaceId!,
                  fileVersionId,
                  statusInReview: issue.statusInReview,
                  reviews: issue.reviews,
                  canEditDefaultStatus: isIssueRequiredPermission(issue, RESOURCE_COMMENT.id),
                }}
                canEdit
                onStatusChanged={refreshIssueAndGrid}
              />
            </div>
            {attachedFiles.length ? (
              <>
                <div className="flex items-center gap-8">
                  <Text className="font-bold">{t("Attachments")}</Text>
                </div>
                <div className="flex flex-col gap-2.5">
                  {attachedFiles.map((file: AttachedFile, index: number) => (
                    <div key={index} className="flex items-center gap-2.5">
                      <ButtonComponent
                        type="button"
                        buttonType={BUTTON_TYPES.TEXT_BUTTON}
                        onClick={() => handleOpenLink(file, false)}
                      >
                        {file.name}
                      </ButtonComponent>
                      <ButtonComponent
                        type="button"
                        buttonType={BUTTON_TYPES.TEXT_BUTTON}
                        onClick={() => handleOpenLink(file, true)}
                      >
                        <BiLinkExternal />
                      </ButtonComponent>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            <div>
              <CustomFields customMetaData={issue.customMetaData} dueDate={issue.dueDate} />
            </div>
          </div>

          {fileVersionId && (
            <div className="pt-7">
              <CommentsList
                issueId={issueId}
                // Branded and nullable on the issue schema; the prop declares a plain `string`.
                offialCommentId={issue.officialCommentId as string | undefined}
                elements={issue.viewpoint ? JSON.parse(issue.viewpoint).selection : {}}
                fileVersionId={fileVersionId}
                showAddComment={false}
              />
            </div>
          )}
          <Accordion
            disableGutters
            expanded={accordionState.access}
            onChange={() => setAccordionState((prev) => ({ ...prev, access: !prev.access }))}
            className="border-0 shadow-none"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0">
              {accessHeader()}
            </AccordionSummary>
            <AccordionDetails>{accessContent()}</AccordionDetails>
          </Accordion>

          {rowData.formTemplate && (
            <Accordion
              disableGutters
              expanded={accordionState.customFields}
              onChange={() => setAccordionState((prev) => ({ ...prev, customFields: !prev.customFields }))}
              className="border-0 shadow-none"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0">
                {customFieldsHeader()}
              </AccordionSummary>
              <AccordionDetails>{customFieldsContent()}</AccordionDetails>
            </Accordion>
          )}
        </div>
      )}
    </div>
  );

  const renderEmptyState = (message: string) => (
    <div className="flex w-160 h-full border border-[#C6D8EC] rounded-lg bg-white items-center justify-center flex-col">
      <IconComponent name="NoData" width="18.75rem" height="18.8125rem" />
      <Text sx={{ textAlign: "center" }}>{message}</Text>
    </div>
  );

  if (sideBarState === "display-row") {
    return renderSideBar();
  }

  if (sideBarState === "no-records") {
    return renderEmptyState(t("No file selected"));
  }

  return renderEmptyState(t("Multiple Files Selected"));
}

export default IssueDetails;
